/**
 * Input validation utilities
 * Provides validation functions for common input types
 */

export interface ValidationResult {
  isValid: boolean;
  error?: string;
}

/**
 * Validates email address
 */
export const validateEmail = (email: string): ValidationResult => {
  if (!email || email.trim() === '') {
    return { isValid: false, error: 'Email is required' };
  }
  
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(email)) {
    return { isValid: false, error: 'Invalid email format' };
  }
  
  return { isValid: true };
};

/**
 * Validates URL
 */
export const validateURL = (url: string): ValidationResult => {
  if (!url || url.trim() === '') {
    return { isValid: false, error: 'URL is required' };
  }
  
  try {
    new URL(url);
    return { isValid: true };
  } catch {
    return { isValid: false, error: 'Invalid URL format' };
  }
};

/**
 * Validates port number
 */
export const validatePort = (port: number | string): ValidationResult => {
  const portNum = typeof port === 'string' ? parseInt(port, 10) : port;
  
  if (isNaN(portNum)) {
    return { isValid: false, error: 'Port must be a number' };
  }
  
  if (portNum < 1 || portNum > 65535) {
    return { isValid: false, error: 'Port must be between 1 and 65535' };
  }
  
  return { isValid: true };
};

/**
 * Validates IP address
 */
export const validateIP = (ip: string): ValidationResult => {
  if (!ip || ip.trim() === '') {
    return { isValid: false, error: 'IP address is required' };
  }
  
  const ipv4Regex = /^(\d{1,3}\.){3}\d{1,3}$/;
  const ipv6Regex = /^([0-9a-fA-F]{1,4}:){7}[0-9a-fA-F]{1,4}$/;
  
  if (ipv4Regex.test(ip)) {
    const parts = ip.split('.');
    const valid = parts.every(part => {
      const num = parseInt(part, 10);
      return num >= 0 && num <= 255;
    });
    if (valid) return { isValid: true };
  }
  
  if (ipv6Regex.test(ip)) {
    return { isValid: true };
  }
  
  return { isValid: false, error: 'Invalid IP address format' };
};

/**
 * Validates project name
 */
export const validateProjectName = (name: string): ValidationResult => {
  if (!name || name.trim() === '') {
    return { isValid: false, error: 'Project name is required' };
  }
  
  if (name.length < 3) {
    return { isValid: false, error: 'Project name must be at least 3 characters' };
  }
  
  if (name.length > 100) {
    return { isValid: false, error: 'Project name must be less than 100 characters' };
  }
  
  // Allow alphanumeric, spaces, hyphens, underscores
  const nameRegex = /^[a-zA-Z0-9\s\-_]+$/;
  if (!nameRegex.test(name)) {
    return { isValid: false, error: 'Project name can only contain letters, numbers, spaces, hyphens, and underscores' };
  }
  
  return { isValid: true };
};

/**
 * Validates ROS2 topic name
 */
export const validateROSTopic = (topic: string): ValidationResult => {
  if (!topic || topic.trim() === '') {
    return { isValid: false, error: 'Topic name is required' };
  }
  
  if (!topic.startsWith('/')) {
    return { isValid: false, error: 'Topic name must start with /' };
  }
  
  // ROS2 topic naming rules
  const topicRegex = /^\/[a-z][a-z0-9_]*(\/[a-z][a-z0-9_]*)*$/;
  if (!topicRegex.test(topic)) {
    return { isValid: false, error: 'Invalid ROS2 topic format. Must be lowercase with underscores' };
  }
  
  return { isValid: true };
};

/**
 * Validates numeric input
 */
export const validateNumber = (
  value: number | string,
  options?: { min?: number; max?: number; required?: boolean }
): ValidationResult => {
  const num = typeof value === 'string' ? parseFloat(value) : value;
  
  if (options?.required && (value === '' || value === null || value === undefined)) {
    return { isValid: false, error: 'This field is required' };
  }
  
  if (value !== '' && value !== null && value !== undefined) {
    if (isNaN(num)) {
      return { isValid: false, error: 'Must be a valid number' };
    }
    
    if (options?.min !== undefined && num < options.min) {
      return { isValid: false, error: `Value must be at least ${options.min}` };
    }
    
    if (options?.max !== undefined && num > options.max) {
      return { isValid: false, error: `Value must be at most ${options.max}` };
    }
  }
  
  return { isValid: true };
};

/**
 * Sanitizes string input to prevent XSS
 */
export const sanitizeString = (input: string): string => {
  if (!input) return '';
  
  // Remove potentially dangerous characters
  return input
    .replace(/[<>]/g, '') // Remove < and >
    .replace(/javascript:/gi, '') // Remove javascript: protocol
    .replace(/on\w+=/gi, '') // Remove event handlers
    .trim();
};

/**
 * Validates and sanitizes user input
 */
export const validateAndSanitize = (input: string, validator: (value: string) => ValidationResult): ValidationResult & { sanitized?: string } => {
  const sanitized = sanitizeString(input);
  const validation = validator(sanitized);
  
  return {
    ...validation,
    sanitized: validation.isValid ? sanitized : undefined,
  };
};

